import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useApplyCouponMutation,
  useRemoveCouponMutation,
} from "../../api/cartApi";

// --- START: MODIFICATION (Helper function added directly to this file) ---
/**
 * Decodes HTML entities from a string.
 * @param {string | undefined} text - The HTML-encoded string.
 * @returns {string} The decoded plain text string.
 */
const decodeHtmlEntities = (text: string | undefined): string => {
  if (!text) return "";

  try {
    // This check is important because this code might run in non-browser environments.
    if (typeof document === "undefined") {
      return text; // Cannot decode without a DOM, return original text.
    }
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  } catch (error) {
    console.error("Failed to decode HTML entities:", error);
    // Fallback to the original text if decoding fails for any reason.
    return text;
  }
};
// --- END: MODIFICATION ---

interface Message {
  type: "success" | "error";
  text: string;
}

interface CouponFormProps {
  appliedCoupons: string[];
  onCouponResult: (message: Message) => void;
  contextualClassName?: string;
}

const CouponForm: React.FC<CouponFormProps> = ({
  appliedCoupons,
  onCouponResult,
  contextualClassName = "",
}) => {
  const { t } = useTranslation();
  const [couponCode, setCouponCode] = useState("");

  const [applyCoupon, { isLoading: isApplying }] = useApplyCouponMutation();
  const [removeCoupon, { isLoading: isRemoving }] = useRemoveCouponMutation();

  const handleApplyCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!couponCode.trim() || isApplying) return;

    try {
      await applyCoupon({ code: couponCode }).unwrap();
      onCouponResult({
        type: "success",
        text: t("cart.couponAppliedSuccess"),
      });
      setCouponCode("");
    } catch (err: any) {
      const backendMessage = err?.data?.message || "";
      const defaultError = t("cart.errors.couponApplyFailed");

      // --- START: MODIFICATION (Use the helper function) ---
      // Decode the message from the backend before using it
      const decodedBackendMessage = decodeHtmlEntities(backendMessage);

      const isMessageEmptyOrInvalid =
        !decodedBackendMessage ||
        decodedBackendMessage.trim() === "" ||
        decodedBackendMessage.toLowerCase().includes("coupon does not exist");

      onCouponResult({
        type: "error",
        text: isMessageEmptyOrInvalid ? defaultError : decodedBackendMessage,
      });
      // --- END: MODIFICATION ---
    }
  };

  const handleRemoveCoupon = async (code: string) => {
    if (isRemoving) return;
    try {
      await removeCoupon({ code }).unwrap();
      onCouponResult({
        type: "success",
        text: t("cart.couponRemovedSuccess"),
      });
    } catch (err) {
      onCouponResult({
        type: "error",
        text: t("cart.errors.couponRemoveFailed"),
      });
    }
  };

  return (
    <div className={`coupon-actions ${contextualClassName}`}>
      <form className="coupon-actions__form" onSubmit={handleApplyCoupon}>
        <input
          type="text"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value)}
          placeholder={t("cart.couponPlaceholder", "Coupon code")}
          disabled={isApplying}
          aria-label={t("cart.couponPlaceholder", "Coupon code")}
        />
        <button
          type="submit"
          className={isApplying ? "is-loading" : ""}
          disabled={isApplying || !couponCode.trim()}
        >
          {isApplying
            ? t("cart.applyingCoupon", "Applying...")
            : t("cart.applyCoupon", "Apply Coupon")}
        </button>
      </form>

      {appliedCoupons.length > 0 && (
        <div className="coupon-actions__applied-list">
          <ul>
            {appliedCoupons.map((code) => (
              <li key={code}>
                <span>{code}</span>
                <button
                  onClick={() => handleRemoveCoupon(code)}
                  disabled={isRemoving}
                  className="remove-coupon-button"
                >
                  {t("cart.removeCoupon", "[Remove]")}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CouponForm;
